<i18n src="./translations"></i18n>

<template>
  <div
    class="pdp-summarized-info"
    :class="classes"
    :data-preload="[$options.name]"
  >
    <div class="pdp-summarized-info__price-info price-info">
      <span
        v-if="!showUpToDiscountedPrice"
        class="price-info__current-price qa-PriceInfo__mainPrice"
      >
        {{ price }}
      </span>

      <PriceDiscounted
        v-else
        :price-texts="explanatoryInformation"
        :unit-prices="unitPrices"
        :prices="prices"
        :price-modifiers="priceModifiers"
      />
    </div>

    <span
      v-if="
        explanatoryInformation.recommendedRetailPrice && !isBusinessCustomer
      "
      class="price-info__uvp qa-PriceInfo__retailPrice"
    >
      {{ $t('pdp-summarized-info.uvp') }}
      <span class="price-info__uvp-price">
        {{ explanatoryInformation.recommendedRetailPrice }}
      </span>
    </span>

    <template v-if="!isBusinessCustomer">
      <price-savings :product="{ priceTexts: explanatoryInformation }" is21 />

      <p
        v-if="hasTaxInformation"
        class="pdp-summarized-info__tax-info tax-info"
      >
        <span
          v-if="pricePerUnit && pricePerUnit.value && pricePerUnit.unit"
          class="tax-info__unit-price qa-PriceInfo__unitPrice"
        >
          {{ pricePerUnit.value }}/{{ pricePerUnit.unit }}
        </span>

        <span
          v-if="explanatoryInformation.tax"
          class="tax-info__tax qa-PriceInfo__tax"
        >
          {{
            $t('pdp-summarized-info.tax_text', {
              '%taxPercentage%': explanatoryInformation.tax,
            })
          }}
        </span>

        <MaskedLink
          class="tax-info__shipping"
          :text="$t('pdp-summarized-info.shipping')"
          :redirect-to="frontFacingPath('content.page.shipping')"
          :with-underline="true"
        />
        <span
          v-if="explanatoryInformation.depositPrice"
          class="tax-info__deposit-price"
        >
          {{ explanatoryInformation.tax ? ', ' : '' }}
          {{
            $t('pdp-summarized-info.deposit_price', {
              '%formattedDepositPrice%': explanatoryInformation.depositPrice,
            })
          }}
        </span>
      </p>
    </template>

    <template v-if="hasExtraInformation">
      <p
        v-if="
          explanatoryInformation.isExchangePart &&
          explanatoryInformation.exchangePartValue !== 0
        "
        class="pdp-summarized-info__text qa-PriceInfo__exchangePart"
      >
        {{
          $t('pdp-summarized-info.exchange_part', {
            '%gross_exchange_part_value%':
              explanatoryInformation.exchangePartValue,
          })
        }}
      </p>
      <p
        v-if="explanatoryInformation.description"
        class="pdp-summarized-info__text qa-PriceInfo__description"
      >
        {{ explanatoryInformation.description }}
      </p>
      <p
        v-if="explanatoryInformation.bulkyPrice"
        class="pdp-summarized-info__text qa-PriceInfo__bulkyPrice"
      >
        {{
          $t('pdp-summarized-info.bulky_price', {
            '%formattedBulkyPrice%': explanatoryInformation.bulkyPrice,
          })
        }}
      </p>
      <p
        v-if="explanatoryInformation.riskyPrice"
        class="pdp-summarized-info__text qa-PriceInfo__bulkyPrice"
      >
        {{
          $t('pdp-summarized-info.risky_price', {
            '%formattedRiskyPrice%': explanatoryInformation.riskyPrice,
          })
        }}
      </p>
    </template>
  </div>
</template>

<script>
import globalMixin from 'Libs/mixins/globalMixin';
import MaskedLink from 'Components/01-atoms/masked-link/MaskedLink';
import { mapState, mapGetters } from 'vuex';
import PriceDiscounted from 'Components/01-atoms/price-discounted/PriceDiscounted';
import PriceSavings from 'Components/01-atoms/price-savings/PriceSavings';

export default {
  name: 'PdpPriceInfo',
  components: { MaskedLink, PriceDiscounted, PriceSavings },
  mixins: [globalMixin],
  props: {
    hasTaxInformation: {
      type: Boolean,
      default: true,
    },
    hasExtraInformation: {
      type: Boolean,
      default: true,
    },
    prices: {
      type: Object,
      default: () => {},
    },
    unitPrices: {
      type: Object,
      default: () => {},
    },
    explanatoryInformation: {
      type: Object,
      default: () => ({
        recommendedRetailPrice: '',
        discountedSalesValueGross: '',
        discountPercentage: 0,
        pricePerUnit: {
          value: 0,
          unit: 0,
        },
        tax: 0,
        depositPrice: 0,
        isExchangePart: false,
        exchangePartValue: 0,
        description: '',
        bulkyPrice: 0,
        riskyPrice: 0,
        upToDiscountPercentage: 0,
      }),
    },
    price: {
      required: true,
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState('renderingSalesChannelConfig', {
      isU2dAktionEnabled: ({ isU2dAktionEnabled }) => isU2dAktionEnabled,
    }),
    ...mapGetters('core', ['isBusinessCustomer']),
    showUpToDiscountedPrice() {
      const discountedSalesValueGross = this.explanatoryInformation
        ?.discountedSalesValueGross;
      const upToDiscountPercentage = this.explanatoryInformation
        ?.upToDiscountPercentage;

      return (
        discountedSalesValueGross &&
        discountedSalesValueGross !== 0 &&
        upToDiscountPercentage &&
        upToDiscountPercentage > 0 &&
        this.isU2dAktionEnabled
      );
    },
    pricePerUnit() {
      return !this.showUpToDiscountedPrice
        ? this.explanatoryInformation.pricePerUnit
        : this.explanatoryInformation.discountedPricePerUnit;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.pdp-summarized-info__price-info {
  display: flex;
  align-items: baseline;
  position: relative;
  margin-bottom: var(--space-0--half);
}

.pdp-summarized-info__text {
  color: var(--color-nero);
  margin: var(--space-2) 0;
  font-size: var(--font-size-SM);
  line-height: var(--font-size-MML);

  &:first-of-type {
    margin-top: var(--space-2);
  }

  &:last-of-type {
    margin-bottom: var(--space-3);
  }
}

.price-info {
  > *:not(:last-child) {
    margin-right: var(--space-2);
  }
}

.price-info__current-price {
  margin-top: var(--space-3) !important;

  font-weight: bold;
  color: var(--color-nero);
  font-size: var(--font-size-LXL);
  line-height: var(--font-size-XL);
}

.price-info__uvp {
  color: var(--color-nero);
  font-size: var(--font-size-M);
  line-height: var(--font-size-MLL);
  white-space: nowrap;
}

.price-info__uvp-price {
  text-decoration: line-through;
}

.price-savings {
  margin-left: 10px;
}

.pdp-summarized-info__tax-info {
  font-size: var(--font-size-SM);
  line-height: var(--font-size-MML);
  color: var(--color-rhino-without-horn);
  margin-bottom: 15px;
  margin-top: var(--space-0--half);
}

.tax-info__shipping {
  display: inline-block;
  text-decoration: underline;
}

@media #{$_mediaMDown} {
  .price-info__current-price {
    font-size: var(--font-size-L);
  }

  .pdp-summarized-info__tax-info {
    margin: var(--space-0--half) 0 var(--space-2) 0;
  }

  .pdp-summarized-info__text {
    margin: var(--space-1--half) 0;

    &:first-of-type {
      margin-top: var(--space-1--half);
    }

    &:last-of-type {
      margin-bottom: var(--space-2);
    }
  }
}
</style>
